import axios from "axios";
import { InvestorPersona, CryptoAsset, PersonaRecommendations } from "../types";

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:3001/api' 
  : (process.env.REACT_APP_API_URL || 'https://agentic-crypto-c9562af36190.herokuapp.com/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increased to 2 minutes for AI recommendations
});

export const apiService = {
  async getPersonas(): Promise<InvestorPersona[]> {
    const response = await api.get<InvestorPersona[]>("/personas");
    return response.data;
  },

  async getPersona(personaId: string): Promise<InvestorPersona> {
    const response = await api.get<InvestorPersona>(`/persona/${personaId}`);
    return response.data;
  },

  async getCryptoAssets(symbols?: string[]): Promise<CryptoAsset[]> {
    const params = symbols?.length ? { symbols: symbols.join(",") } : {};
    const response = await api.get<CryptoAsset[]>("/crypto-assets", { params });
    return response.data;
  },

  async getRecommendations(
    personaId: string,
    assetSymbols?: string[],
    retryCount = 0
  ): Promise<PersonaRecommendations> {
    try {
      const response = await api.post<PersonaRecommendations>(
        "/recommendations",
        {
          personaId,
          assetSymbols,
        }
      );
      return response.data;
    } catch (error: any) {
      // Retry logic for timeouts or server errors
      if (
        retryCount < 2 &&
        (error.code === "ECONNABORTED" || error.response?.status >= 500)
      ) {
        console.warn(
          `Retrying recommendation for persona ${personaId}, attempt ${
            retryCount + 1
          }`
        );
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * (retryCount + 1))
        ); // Progressive delay
        return this.getRecommendations(personaId, assetSymbols, retryCount + 1);
      }
      throw error;
    }
  },

  async getMarketAnalysis(): Promise<{
    analysis: string;
    assets: CryptoAsset[];
    timestamp: Date;
  }> {
    const response = await api.get("/market-analysis");
    return response.data;
  },

  async healthCheck(): Promise<{ status: string; timestamp: Date }> {
    const response = await api.get("/health");
    return response.data;
  },
};
